import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface PowMiningBarProps {
  tries: number;
  difficulty: number;
  onCancel: () => void;
  /** ms epoch when mining started — drives elapsed + ETA. */
  startedAt: number;
}

export function PowMiningBar({ tries, difficulty, onCancel, startedAt }: PowMiningBarProps) {
  const [now, setNow] = useState(() => Date.now());

  // Local 500ms tick so elapsed/ETA stay fresh while mining.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);

  // Expected tries ≈ 2^difficulty; progress capped at 95%.
  const expected = Math.pow(2, difficulty);
  const pct = Math.min(95, (tries / expected) * 100);

  // Elapsed + ETA (guarded against zero elapsed / zero tries).
  const elapsedSec = Math.max(0, (now - startedAt) / 1000);
  const rate = elapsedSec > 0 ? tries / elapsedSec : 0;
  const etaSec = rate > 0 ? Math.max(0, Math.round(expected / rate - elapsedSec)) : null;

  return (
    <div className="flex items-center gap-3 p-2 border border-gray-200 dark:border-[#2a2d30] rounded bg-gray-50 dark:bg-[#1a1c1e]">
      <div className="flex-1">
        <div className="text-[12px] text-gray-600 dark:text-gray-400 mb-1">
          Mining {difficulty}-bit PoW… {tries} tries · {Math.floor(elapsedSec)}s elapsed
          {etaSec !== null ? ` · ~${etaSec}s left` : ''}
        </div>
        <Progress value={pct} className="h-1.5" />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="h-7 px-2 text-[12px] text-gray-600 dark:text-gray-400"
        aria-label="Cancel mining"
      >
        <X className="size-3.5 mr-1" />
        Cancel
      </Button>
    </div>
  );
}
